import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { InstagramIcon, UserPlusIcon, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
interface Model {
  name: string;
  instagram: string;
  link: string;
  profileImage: string;
  gallery: string[];
}
const confirmedModels: Model[] = [{
  name: 'Caro',
  instagram: 'mrsloesel',
  link: 'https://www.instagram.com/mrsloesel/',
  profileImage: 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-1.jpg',
  gallery: ['https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-1.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-2.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-3.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-4.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-5.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-6.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/caro/caro-7.jpg']
}, {
  name: 'Jessica',
  instagram: 'jessi.a',
  link: 'https://www.instagram.com/jessi.a/',
  profileImage: 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-1.jpg',
  gallery: ['https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-1.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-2.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-3.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-4.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-5.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/jessica/jessica-6.jpg']
}, {
  name: 'Estephania',
  instagram: 'estephania_ha',
  link: 'https://www.instagram.com/estephania_ha/',
  profileImage: 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-1.jpg',
  gallery: ['https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-1.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-2.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-3.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-4.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-5.jpg', 'https://cdn.rawproductionsgmbh.de/shootingtrip/portugalmai2026/models/estephania/estephania-6.jpg']
}];
const EMPTY_SLOTS = 3;
function GalleryLightbox({
  model,
  isOpen,
  onClose
}: {
  model: Model | null;
  isOpen: boolean;
  onClose: () => void;
}) {
  const [currentIndex, setCurrentIndex] = useState(0);
  // Reset index when model changes
  useEffect(() => {
    setCurrentIndex(0);
  }, [model]);
  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  // Keyboard navigation
  useEffect(() => {
    if (!isOpen || !model) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrentIndex(i => (i + 1) % model.gallery.length);
      if (e.key === 'ArrowLeft') setCurrentIndex(i => (i - 1 + model.gallery.length) % model.gallery.length);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, model, onClose]);
  if (!model) return null;
  const goNext = () => setCurrentIndex(i => (i + 1) % model.gallery.length);
  const goPrev = () => setCurrentIndex(i => (i - 1 + model.gallery.length) % model.gallery.length);
  return <AnimatePresence data-id="element-100">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/85 backdrop-blur-md z-[70]" onClick={onClose} data-id="element-101" />

          {/* Lightbox — use inset + flex centering for reliable positioning */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0,
        scale: 0.95
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="fixed inset-0 z-[71] flex items-center justify-center p-4 md:p-8 pointer-events-none" data-id="element-102">
            <div className="w-full max-w-4xl flex flex-col pointer-events-auto" style={{
          maxHeight: 'calc(100vh - 4rem)'
        }} onClick={e => e.stopPropagation()} data-id="element-103">
              {/* Header */}
              <div className="flex items-center justify-between mb-3 shrink-0" data-id="element-104">
                <div className="flex items-center gap-3" data-id="element-105">
                  <h3 className="text-white font-heading text-xl" data-id="element-106">
                    {model.name}
                  </h3>
                  <a href={model.link} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-brand-accent transition-colors text-sm flex items-center gap-1.5" data-id="element-107">
                    <InstagramIcon size={14} data-id="element-108" />@{model.instagram}
                  </a>
                </div>
                <button onClick={onClose} className="w-10 h-10 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors" aria-label="Schließen" data-id="element-109">
                  <XIcon size={20} data-id="element-110" />
                </button>
              </div>

              {/* Main Image */}
              <div className="relative rounded-xl overflow-hidden bg-black/50 shrink min-h-0 flex-1" data-id="element-111">
                <AnimatePresence mode="wait" data-id="element-112">
                  <motion.img key={currentIndex} src={model.gallery[currentIndex]} alt={`${model.name} - Bild ${currentIndex + 1}`} initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.25
              }} className="w-full h-full object-contain" data-id="element-113" />
                </AnimatePresence>

                {/* Nav arrows */}
                <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/60 transition-colors" aria-label="Vorheriges Bild" data-id="element-114">
                  <ChevronLeftIcon size={20} data-id="element-115" />
                </button>
                <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white/80 hover:text-white hover:bg-black/60 transition-colors" aria-label="Nächstes Bild" data-id="element-116">
                  <ChevronRightIcon size={20} data-id="element-117" />
                </button>

                {/* Counter */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white/80 text-xs" data-id="element-118">
                  {currentIndex + 1} / {model.gallery.length}
                </div>
              </div>

              {/* Thumbnails */}
              <div className="flex gap-2 mt-3 overflow-x-auto hide-scrollbar pb-1 shrink-0" data-id="element-119">
                {model.gallery.map((img, i) => <button key={i} onClick={() => setCurrentIndex(i)} className={`shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-lg overflow-hidden border-2 transition-all ${i === currentIndex ? 'border-brand-accent' : 'border-transparent opacity-50 hover:opacity-100'}`} data-id="element-120">
                    <img src={img} alt="" className="w-full h-full object-cover" data-id="element-121" />
                  </button>)}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function ConfirmedModels() {
  const [lightboxModel, setLightboxModel] = useState<Model | null>(null);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <>
      <section id="models" className="py-24 md:py-32 px-6 bg-white" data-id="element-122">
        <div className="max-w-7xl mx-auto" data-id="element-123">
          <div className="text-center mb-16" data-id="element-124">
            <span className="block text-xs font-body tracking-[0.3em] text-brand-accent uppercase mb-4" data-id="element-125">
              Bereits dabei
            </span>
            <h2 className="text-4xl md:text-5xl font-heading text-[#1a1a2e] mb-4" data-id="element-126">
              Diese Models sind bestätigt.
            </h2>
            <p className="text-[#4a4a5a] font-light max-w-2xl mx-auto" data-id="element-127">
              Lerne die Kreativen kennen, die bei der nächsten Reise dabei sind
              — und sichere dir einen der verbleibenden Plätze.
            </p>
          </div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={containerVariants} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5" data-id="element-128">
            {/* Confirmed models */}
            {confirmedModels.map(model => <motion.div key={model.name} variants={cardVariants} className="group cursor-pointer" onClick={() => setLightboxModel(model)} data-id="element-129">
                <div className="relative aspect-[3/4] rounded-xl overflow-hidden mb-4 shadow-sm group-hover:shadow-md transition-shadow duration-300" data-id="element-130">
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 z-10 flex items-center justify-center" data-id="element-131">
                    <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 tracking-wider uppercase" data-id="element-132">
                      Galerie ansehen
                    </span>
                  </div>
                  <img src={model.profileImage} alt={model.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" data-id="element-133" />
                </div>
                <h3 className="font-heading text-lg text-[#1a1a2e] mb-0.5" data-id="element-134">
                  {model.name}
                </h3>
                <a href={model.link} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 text-xs text-[#8a8a9a] hover:text-brand-accent transition-colors" data-id="element-135">
                  <InstagramIcon size={12} data-id="element-136" />@{model.instagram}
                </a>
              </motion.div>)}

            {/* Empty placeholder slots */}
            {Array.from({
            length: EMPTY_SLOTS
          }).map((_, index) => <motion.div key={`empty-${index}`} variants={cardVariants} data-id="element-137">
                <a href="#bewerbung" className="block group" data-id="element-138">
                  <div className="relative aspect-[3/4] rounded-xl border-2 border-dashed border-[#e2e2e6] group-hover:border-brand-accent/40 mb-4 flex flex-col items-center justify-center transition-colors duration-300 bg-[#fafafa]" data-id="element-139">
                    <div className="w-12 h-12 rounded-full bg-[#f0f0f2] group-hover:bg-brand-accent/10 flex items-center justify-center mb-3 transition-colors duration-300" data-id="element-140">
                      <UserPlusIcon size={20} className="text-[#c0c0c8] group-hover:text-brand-accent transition-colors duration-300" data-id="element-141" />
                    </div>
                    <span className="text-xs text-[#c0c0c8] group-hover:text-brand-accent font-medium tracking-wide transition-colors duration-300" data-id="element-142">
                      Dein Platz?
                    </span>
                  </div>
                  <p className="font-heading text-lg text-[#d0d0d4] mb-0.5" data-id="element-143">
                    Offen
                  </p>
                  <p className="text-xs text-[#d0d0d4]" data-id="element-144">Jetzt bewerben →</p>
                </a>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      <GalleryLightbox model={lightboxModel} isOpen={lightboxModel !== null} onClose={() => setLightboxModel(null)} data-id="element-145" />
    </>;
}